import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Campaigns API
export const campaignsAPI = {
  getAll: () => api.get('/campaigns'),
  getFiltered: (params) => api.get('/campaigns', { params }),  // With advertiser_id filter
  getById: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
  getModes: () => api.get('/campaign-modes'),
  syncData: (id) => api.post(`/sync-campaign/${id}`),
  syncAll: () => api.post('/sync-all-campaigns'),
  // Campaign control (start/stop)
  controlPlatform: (campaignId, platform, action) => 
    api.post(`/campaigns/${campaignId}/control/${platform}`, { action }),
  controlAll: (campaignId, action) => 
    api.post(`/campaigns/${campaignId}/control-all`, { action }),
  // Bulk assign to advertiser
  bulkAssign: (campaignIds, advertiserId) =>
    api.post('/campaigns/bulk-assign', { campaign_ids: campaignIds, advertiser_id: advertiserId }),
};

// Currency API
export const currencyAPI = {
  getExchangeRates: (baseCurrency = 'USD') => api.get(`/exchange-rates/${baseCurrency}`),
  convert: (amount, fromCurrency, toCurrency) => api.post('/convert-currency', {
    amount,
    from_currency: fromCurrency,
    to_currency: toCurrency
  }),
  convertMultiple: (amounts, toCurrency) => api.post('/convert-multiple-currencies', {
    amounts,
    to_currency: toCurrency
  }),
  getUnifiedSpend: (displayCurrency = 'USD', params = {}) => api.get('/dashboard/unified-spend', {
    params: { display_currency: displayCurrency, ...params }
  }),
};

// Campaign Groups API (Hybrid System)
export const campaignGroupsAPI = {
  getAll: () => api.get('/campaign-groups'),
  getById: (id) => api.get(`/campaign-groups/${id}`),
  create: (data) => api.post('/campaign-groups', data),
  update: (id, data) => api.put(`/campaign-groups/${id}`, data),
  delete: (id) => api.delete(`/campaign-groups/${id}`),
  getAvailableCampaigns: () => api.get('/available-campaigns-for-grouping'),
};

// Platform Integrations API
export const platformIntegrationsAPI = {
  getConfig: () => api.get('/platform-integrations/config'),
  getAll: () => api.get('/platform-integrations'),
  save: (data) => api.post('/platform-integrations', data),
  delete: (platform) => api.delete(`/platform-integrations/${platform}`),
  test: (platform) => api.post(`/platform-integrations/${platform}/test`),
  sync: (platform) => api.post(`/platform-integrations/${platform}/sync`),
  getSyncStatus: () => api.get('/sync-status'),
  syncAll: () => api.post('/sync-all'),
  clearDemoData: () => api.delete('/clear-demo-data'),
  // Platform health monitoring
  getHealth: () => api.get('/platform-health'),
  triggerHealthCheck: () => api.post('/platform-health/check'),
  getAlertHistory: (limit = 10) => api.get(`/platform-alerts/history?limit=${limit}`),
};

// Platform Data API
export const platformDataAPI = {
  getAll: (params) => api.get('/platform-data', { params }),
  create: (data) => api.post('/platform-data', data),
  update: (id, data) => api.put(`/platform-data/${id}`, data),
  delete: (id) => api.delete(`/platform-data/${id}`),
};

// Advertisers API
export const advertisersAPI = {
  getAll: () => api.get('/advertisers'),
  getById: (id) => api.get(`/advertisers/${id}`),
  create: (data) => api.post('/advertisers', data),
  update: (id, data) => api.put(`/advertisers/${id}`, data),
  delete: (id) => api.delete(`/advertisers/${id}`),
  // Integrations
  getIntegrations: (advertiserId) => api.get(`/advertisers/${advertiserId}/integrations`),
  addIntegration: (advertiserId, data) => api.post(`/advertisers/${advertiserId}/integrations`, data),
  updateIntegration: (advertiserId, integrationId, data) => api.put(`/advertisers/${advertiserId}/integrations/${integrationId}`, data),
  testIntegration: (advertiserId, integrationId) => api.post(`/advertisers/${advertiserId}/integrations/${integrationId}/test`),
  syncIntegration: (advertiserId, integrationId) => api.post(`/advertisers/${advertiserId}/integrations/${integrationId}/sync`),
  deleteIntegration: (advertiserId, integrationId) => api.delete(`/advertisers/${advertiserId}/integrations/${integrationId}`),
  // Users
  getUsers: (advertiserId) => api.get(`/advertisers/${advertiserId}/users`),
  addUser: (advertiserId, data) => api.post(`/advertisers/${advertiserId}/users`, data),
  deleteUser: (advertiserId, userId) => api.delete(`/advertisers/${advertiserId}/users/${userId}`),
  // Platform access
  getEffectivePlatforms: (advertiserId) => api.get(`/advertisers/${advertiserId}/effective-platforms`),
  updateUserPlatforms: (advertiserId, userId, platforms) => api.put(`/advertisers/${advertiserId}/users/${userId}/platforms`, platforms),
  // Analytics
  getAnalytics: (advertiserId, params) => api.get(`/advertisers/${advertiserId}/analytics`, { params }),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: (params) => api.get('/dashboard/summary', { params }),
  getPlatformSummary: (params) => api.get('/dashboard/platform-summary', { params }),
  getCampaignSummary: (params) => api.get('/dashboard/campaign-summary', { params }),
  getTrend: (params) => api.get('/dashboard/trend', { params }),
  getPlatforms: () => api.get('/platforms'),
  seedData: () => api.post('/seed-data'),
  removeDemoData: () => api.delete('/demo-data'),
  getDV360SourceDomain: (params) => api.get('/dv360/source-domain-report', { params }),
};

// Report Schedules API
export const reportSchedulesAPI = {
  getAll: () => api.get('/report-schedules'),
  create: (data) => api.post('/report-schedules', data),
  update: (id, data) => api.put(`/report-schedules/${id}`, data),
  delete: (id) => api.delete(`/report-schedules/${id}`),
  sendReport: (data) => api.post('/send-report', data),
  getEmailStatus: () => api.get('/email-config-status'),
};

// Custom Fields API
export const customFieldsAPI = {
  getAll: (entityType) => api.get('/custom-fields', { params: { entity_type: entityType } }),
  create: (data) => api.post('/custom-fields', data),
  update: (id, data) => api.put(`/custom-fields/${id}`, data),
  delete: (id) => api.delete(`/custom-fields/${id}`),
  getValues: (entityType, entityId) => api.get(`/custom-fields/values/${entityType}/${entityId}`),
  saveValues: (entityType, entityId, values) => api.put(`/custom-fields/values/${entityType}/${entityId}`, values),
};

// Data Import API
export const importAPI = {
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importCampaigns: (file, advertiserId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/campaigns', formData, {
      params: { advertiser_id: advertiserId },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importPerformanceData: (file, platform, advertiserId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/performance-data', formData, {
      params: { platform, advertiser_id: advertiserId },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importAdvertisers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/advertisers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getTemplate: (dataType) => api.get(`/import/templates/${dataType}`),
};

// Alerts API
export const alertsAPI = {
  getRules: (entityType) => api.get('/alerts/rules', { params: { entity_type: entityType } }),
  createRule: (data) => api.post('/alerts/rules', data),
  updateRule: (id, data) => api.put(`/alerts/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/alerts/rules/${id}`),
  testRule: (id) => api.post(`/alerts/rules/${id}/test`),
  getNotifications: (unreadOnly) => api.get('/alerts/notifications', { params: { unread_only: unreadOnly } }),
  getUnreadCount: () => api.get('/alerts/notifications/unread-count'),
  markRead: (id) => api.put(`/alerts/notifications/${id}/read`),
  markAllRead: () => api.put('/alerts/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/alerts/notifications/${id}`),
  getHistory: (ruleId) => api.get('/alerts/history', { params: { rule_id: ruleId } }),
  runCheck: () => api.post('/alerts/check'),
};

// Custom Metrics API
export const customMetricsAPI = {
  getAll: (showOn) => api.get('/custom-metrics', { params: { show_on: showOn } }),
  getBaseMetrics: () => api.get('/custom-metrics/base-metrics'),
  create: (data) => api.post('/custom-metrics', data),
  update: (id, data) => api.put(`/custom-metrics/${id}`, data),
  delete: (id) => api.delete(`/custom-metrics/${id}`),
  calculate: (data, metricIds) => api.post('/custom-metrics/calculate', data, { params: { metric_ids: metricIds } }),
  previewFormula: (expression, sampleData) => api.post('/custom-metrics/preview', null, { 
    params: { expression },
    data: sampleData 
  }),
};

export default api;
